import {testIdProps} from '@github-ui/test-id-props'
import {CalendarIcon, RocketIcon, SingleSelectIcon} from '@primer/octicons-react'
import {Octicon} from '@primer/react/deprecated'
import {clsx} from 'clsx'
import type {ElementType, ReactNode} from 'react'

import type {MemexStatus} from '../../../api/memex/contracts'
import {formatISODateString} from '../../../helpers/parsing'
import {SanitizedMarkdownViewer} from '../../common/sanitized-markdown-viewer'
import {SingleSelectToken} from '../../fields/single-select/single-select-token'
import styles from './body.module.css'

type Props = {
  status: MemexStatus
}

const ItemDetailsRow = ({
  label,
  icon,
  children,
  testId,
}: {
  label: string
  icon: ElementType
  children: ReactNode
  testId: string
}) => {
  return (
    <div className={styles.detailRow}>
      <p className={styles.detailLabel}>
        <Octicon icon={icon} className={styles.detailIcon} />
        {label}:
      </p>
      <div {...testIdProps(`status-update-item-value-${testId}`)} className={styles.detailValue}>
        {children}
      </div>
    </div>
  )
}

export const StatusUpdateItemBody = ({status}: Props) => {
  const {startDate, targetDate, status: statusOption} = status.statusValue
  const {bodyHtml} = status
  const hasStatusValues = !!startDate || !!targetDate || !!statusOption

  return (
    <div className={styles.container}>
      {startDate && (
        <ItemDetailsRow label="Start date" icon={CalendarIcon} testId="start-date">
          {formatISODateString(new Date(startDate))}
        </ItemDetailsRow>
      )}
      {targetDate && (
        <ItemDetailsRow label="Target date" icon={RocketIcon} testId="target-date">
          {formatISODateString(new Date(targetDate))}
        </ItemDetailsRow>
      )}
      {status && statusOption && (
        <ItemDetailsRow label="Status" icon={SingleSelectIcon} testId="status">
          <SingleSelectToken option={statusOption} />
        </ItemDetailsRow>
      )}
      {bodyHtml && (
        <div
          className={clsx(
            styles.bodyContent,
            hasStatusValues ? styles.bodyContentWithStatus : styles.bodyContentNoStatus,
          )}
          {...testIdProps(`status-update-item-value-body`)}
        >
          <SanitizedMarkdownViewer unverifiedHTML={bodyHtml} loading={false} />
        </div>
      )}
    </div>
  )
}

try{ ItemDetailsRow.displayName ||= 'ItemDetailsRow' } catch {}
try{ StatusUpdateItemBody.displayName ||= 'StatusUpdateItemBody' } catch {}